import { useCallback, useState } from 'react';
import { Field } from 'react-final-form';
import { connect } from 'react-redux';
import cn from 'classnames';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { MspCodesEnum } from 'APIPackage/KnmModule/enums/MspCodesEnum';
import { DATE_FORMAT_ISO_DOT_FNS, DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmAddressTypeSelectOption';
import { KNM_VERIFIED_PERSON, KNM_PREVIOUS } from 'CorePackage/MainModule/routes/routes';
import {
  INN_LABEL_BLOCK_ID,
  OGRN_LABEL_BLOCK_ID,
  ORGANIZATION_NAME_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { NotMatchingFnsFieldTypesEnum } from 'KnmPackage/MainModule/enums/NotMatchingFnsFieldTypesEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { mspCategoryTitlesByMspCodes } from 'KnmPackage/MainModule/constants/mspCategoryTitlesByMspCodes';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import {
  getKnmErpDangerClassesSelectOptions,
  getKnmErpRiskCategoriesSelectOptions,
  getKnmFormRef,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { Section } from 'UIPackage/FormModule/components/Section';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { getDecisionKnoSupervisionHazardClassApplies } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';

import { FieldReplaceAlert } from './FieldReplaceAlert';
import { KnmErpObjects } from './KnmErpObjects';
import { KnmErpRegistryCheck } from './KnmErpRegistryCheck';
import { KnmErpSubjectReplaceData } from './KnmErpSubjectReplaceData';
import styles from './KnmErpSubject.module.scss';
import { getCatalogKnmAddressTypesSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';

interface IKnmErpSubjectProps {
  inn: string | undefined;
  knmCreatedDate: string | null | undefined;
  newKnm: boolean;
  notMatchingFnsFields: NotMatchingFnsFieldTypesEnum[] | undefined;
  objects: IKnmForm['objects'];
  ogrn: string | undefined;
  organizationAutocomplete: IKnmForm['organizationAutocomplete'];
  organizationName: IKnmForm['organizationName'];
  registryLegalAddressFns: string | undefined;
  registryOrganizationNameFns: string | undefined;
  watchOnly: boolean;
  settings: IAdminSetting[];
}

interface IKnmErpSubjectStoreProps {
  formRef: IKnmFormRef | undefined;
  knmAddressTypesSelectOptions: IKnmAddressTypeSelectOption[];
  riskCategoriesSelectOptionsErvk: IDictionaryRiskCategorySelectOption[] | undefined;
  dangerClassesSelectOptionsErvk: IDictionaryDangerClassSelectOption[] | undefined;
  hazardClassApplies: boolean | undefined;
}

const KnmErpSubjectWithoutStore = ({
  inn,
  knmCreatedDate,
  newKnm,
  notMatchingFnsFields,
  objects,
  ogrn,
  organizationAutocomplete,
  organizationName,
  registryLegalAddressFns,
  registryOrganizationNameFns,
  watchOnly,
  settings,
  formRef,
  knmAddressTypesSelectOptions,
  riskCategoriesSelectOptionsErvk,
  dangerClassesSelectOptionsErvk,
  hazardClassApplies,
}: IKnmErpSubjectProps & IKnmErpSubjectStoreProps) => {
  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();

  const [isReplaceAlertsVisible, setIsReplaceAlertsVisible] = useState(false);
  const showReplaceAlerts = useCallback(() => setIsReplaceAlertsVisible(true), []);
  const hideReplaceAlerts = useCallback(() => setIsReplaceAlertsVisible(false), []);

  const isOrganizationNameFieldReplacing = Boolean(
    registryOrganizationNameFns && organizationName !== registryOrganizationNameFns,
  );
  const isOrganizationNameFieldAlertVisible = isReplaceAlertsVisible && isOrganizationNameFieldReplacing;
  const isUseERVKParamTables = isLaterThanAdminSetting(
    knmCreatedDate,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );

  const handleOrganizationNameReplaceButtonClick = useCallback(() => {
    formRef?.current.change('organizationName', registryOrganizationNameFns);
  }, [formRef, registryOrganizationNameFns]);

  const handleLegalEntityAutoCompleteSelect = useCallback(
    (legalEntityLookup: ILegalEntityLookup) => {
      formRef?.current.change('organizationAutocomplete', true);
      if (legalEntityLookup.ogrn) {
        formRef?.current.change('ogrn', legalEntityLookup.ogrn);
      }
      if (legalEntityLookup.inn) {
        formRef?.current.change('inn', legalEntityLookup.inn);
      }
      if (legalEntityLookup.orgName) {
        formRef?.current.change('organizationName', legalEntityLookup.orgName);
      }
      if (legalEntityLookup.msp) {
        formRef?.current.change('isSmallEnterpriseText', legalEntityLookup.msp);
      }
      if (legalEntityLookup.dateReg) {
        formRef?.current.change(
          'registrationDate',
          formatDateLocale({
            date: legalEntityLookup.dateReg,
            dateFormat: DATE_FORMAT_LONG_FNS,
            parseFormat: DATE_FORMAT_ISO_DOT_FNS,
          }) || '',
        );
      }
      if (legalEntityLookup.status) {
        formRef?.current.change('organizationStatus', legalEntityLookup.status);
      }
      if (legalEntityLookup.mspCategory) {
        formRef?.current.change(
          'mspCode',
          mspCodesByMspCategoryDigitCodes[Number(legalEntityLookup.mspCategory) as MspCategoryDigitCodesEnum],
        );
      }
      if (legalEntityLookup.mspRegistryDate) {
        formRef?.current.change('mspRegistryDate', legalEntityLookup.mspRegistryDate);
      }
    },
    [formRef, knmAddressTypesSelectOptions],
  );

  const handleAutoCompleteClear = useCallback(() => {
    formRef?.current.change('ogrn');
    formRef?.current.change('inn');
    formRef?.current.change('organizationName');
    const autocompleteObjectIndex = objects?.findIndex(object => object.autocomplete);

    formRef?.current.change('organizationAutocomplete', false);
    formRef?.current.change('mspCode');
    if (typeof autocompleteObjectIndex === 'number' && autocompleteObjectIndex >= 0) {
      const autocompleteFieldName = `objects[${autocompleteObjectIndex}].autocomplete` as keyof IKnmForm;

      formRef?.current.change(autocompleteFieldName, false);
    }
  }, [formRef, objects]);

  return (
    <Section title="Информация о проверяемом лице" id={KNM_VERIFIED_PERSON}>
      <Row>
        <FieldBlock
          label="Основной государственный регистрационный номер (ОГРН)"
          id={OGRN_LABEL_BLOCK_ID}
          size="half"
          color="black"
        >
          <KnmOgrnField
            name="ogrn"
            clearable={watchOnly ? false : organizationAutocomplete}
            disabled={watchOnly || organizationAutocomplete}
            onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
            onClear={handleAutoCompleteClear}
            ogrn={ogrn}
            onLoadingChange={setFnsLookupByOgrnLoadingFlag}
          />
        </FieldBlock>
        <FieldBlock
          label="Идентификационный номер налогоплательщика (ИНН)"
          id={INN_LABEL_BLOCK_ID}
          size="half"
          color="black"
        >
          <KnmInnField
            name="inn"
            isErp
            clearable={watchOnly ? false : organizationAutocomplete}
            disabled={watchOnly || organizationAutocomplete}
            onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
            inn={inn}
            onClear={handleAutoCompleteClear}
            onLoadingChange={setFnsLookupByInnLoadingFlag}
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock label="Наименование проверяемого лица" id={ORGANIZATION_NAME_BLOCK_ID} color="black">
          <TextareaField
            inputClassName={styles.OrganizationNameInput}
            name="organizationName"
            withWarning={isOrganizationNameFieldAlertVisible}
            clearable={watchOnly ? false : organizationAutocomplete}
            disabled={watchOnly || organizationAutocomplete}
            onClear={handleAutoCompleteClear}
          />
          {isOrganizationNameFieldAlertVisible && (
            <FieldReplaceAlert
              className={styles.FieldReplaceAlert}
              text={`Значение ЕГРЮЛ/ЕГРИП: ${registryOrganizationNameFns}`}
              onReplaceButtonClick={handleOrganizationNameReplaceButtonClick}
            />
          )}
        </FieldBlock>
      </Row>

      <Row>
        <FieldBlock label="Наличие проверяемого лица в реестре МСП:" color="black" placementLabel="left">
          <TextInputField className="shared-row-field" name="isSmallEnterpriseText" disabled />
        </FieldBlock>
      </Row>

      <Row title="Категория субъекта МСП">
        <Field id="mspCode" name="mspCode">
          {props => (
            <span className={cn('shared-field-row-block', styles.MspCodeField)}>
              {mspCategoryTitlesByMspCodes[props.input.value as MspCodesEnum]}
            </span>
          )}
        </Field>
      </Row>

      {organizationAutocomplete && (
        <Row>
          <FieldBlock label="Дата регистрации" color="black" size="half">
            <DatePickerField name="registrationDate" disabled={watchOnly || organizationAutocomplete} />
          </FieldBlock>
          <FieldBlock label="Статус юридического лица" color="black" size="half">
            <TextInputField name="organizationStatus" disabled={watchOnly || organizationAutocomplete} />
          </FieldBlock>
        </Row>
      )}

      <Row>
        <a className={styles.HistoryLink} href={`#${KNM_PREVIOUS}`}>
          Просмотреть ранее проведенные КНМ в отношении проверяемого лица
        </a>
      </Row>
      <Row>
        <KnmErpRegistryCheck newKnm={newKnm} inn={inn} ogrn={ogrn} />
      </Row>

      {notMatchingFnsFields && notMatchingFnsFields.length > 0 && (
        <KnmErpSubjectReplaceData
          className={styles.ReplaceAlert}
          isReplaceAlertsVisible={isReplaceAlertsVisible}
          notMatchingFnsFields={notMatchingFnsFields}
          objects={objects}
          organizationName={organizationName}
          registryLegalAddressFns={registryLegalAddressFns}
          registryOrganizationNameFns={registryOrganizationNameFns}
          onHideButtonClick={hideReplaceAlerts}
          onShowButtonClick={showReplaceAlerts}
        />
      )}

      {isUseERVKParamTables && (
        <Row>
          <FieldBlock
            label={hazardClassApplies ? 'Класс опасности' : 'Категория риска'}
            placementLabel="left"
            color="black"
            labelSize="col-4"
          >
            <SelectInputField
              className="shared-row-field"
              name="riskCategory"
              options={hazardClassApplies ? dangerClassesSelectOptionsErvk : riskCategoriesSelectOptionsErvk}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>
      )}

      {!isUseERVKParamTables && (
        <Row>
          <FieldBlock label="Категория риска" placementLabel="left" color="black" labelSize="col-4">
            <SelectInputFieldAutoOptions
              className="shared-row-field"
              name="riskCategory"
              optionName={CommonCatalogsNamesEnum.RISK_CATEGORIES}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>
      )}

      <KnmErpObjects
        registryLegalAddressFns={registryLegalAddressFns}
        replaceAlertsVisible={isReplaceAlertsVisible}
        watchOnly={watchOnly}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpSubjectStoreProps => ({
  formRef: getKnmFormRef(state),
  knmAddressTypesSelectOptions: getCatalogKnmAddressTypesSelectOptions(state),
  riskCategoriesSelectOptionsErvk: getKnmErpRiskCategoriesSelectOptions(state),
  dangerClassesSelectOptionsErvk: getKnmErpDangerClassesSelectOptions(state),
  hazardClassApplies: getDecisionKnoSupervisionHazardClassApplies(state),
});

export const KnmErpSubject = connect(mapStateToProps)(KnmErpSubjectWithoutStore);
